import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import Config from "../../Config";

import PrimaryButton from "../../components/Buttons/PrimaryButton";

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const { login, loginWithKeycloak, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const emailVerified = sessionStorage.getItem("emailVerified");
    if (emailVerified === "true") {
      setSuccessMessage(
        "Email verified successfully! Please sign in to continue.",
      );
      sessionStorage.removeItem("emailVerified");
    }
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const pendingOrgSetupToken = sessionStorage.getItem(
        "pendingOrgSetupToken",
      );
      const pendingPlan = localStorage.getItem("pendingPlan");
      if (pendingOrgSetupToken) {
        navigate(`/organization/setup?token=${pendingOrgSetupToken}`, {
          replace: true,
        });
      } else if (pendingPlan === "individual") {
        navigate("/card-details", { replace: true });
      } else {
        navigate("/home/dashboard", { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleKeycloakLogin = async () => {
    setError("");
    setLoading(true);
    try {
      await loginWithKeycloak();
    } catch (error: any) {
      console.error("Keycloak login error:", error);
      setError("Failed to initiate PSDI login. Please try again.");
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setError("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    try {
      await login(email, password);
      // Check if there's a pending organization setup
      const pendingOrgSetupToken = sessionStorage.getItem(
        "pendingOrgSetupToken",
      );
      const pendingPlan = localStorage.getItem("pendingPlan");
      if (pendingOrgSetupToken) {
        navigate(`/organization/setup?token=${pendingOrgSetupToken}`);
      } else if (pendingPlan === "individual") {
        navigate("/card-details");
      } else {
        navigate("/home/dashboard");
      }
    } catch (error: any) {
      console.error("Login error:", error);

      if (error.code) {
        switch (error.code) {
          case "auth/user-not-found":
          case "auth/wrong-password":
          case "auth/invalid-credential":
            setError("Incorrect email or password");
            break;
          case "auth/too-many-requests":
            setError("Too many attempts. Please try again later.");
            break;
          case "auth/user-disabled":
            setError("This account has been disabled.");
            break;
          default:
            setError(error.message || "Login failed");
        }
      } else if (error.message) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-white">
      <div className="w-full max-w-sm px-4">
        <h2 className="text-xl font-semibold tracking-tight text-gray-900 mb-1">
          Sign in to your account
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          Welcome back. Enter your details below.
        </p>

        {Config.KEYCLOAK_ENABLED && (
          <>
            <button
              onClick={handleKeycloakLogin}
              disabled={loading}
              className="flex justify-center items-center gap-2 border border-gray-200 rounded-lg p-2.5 w-full bg-white hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60 text-sm font-medium text-gray-700"
            >
              <img src="./psdi-icon.png" alt="PSDI logo" width={18} />
              Sign in with PSDI
            </button>

            <div className="flex items-center my-5">
              <div className="flex-grow border-t border-gray-100" />
              <span className="mx-3 text-xs text-gray-400">or</span>
              <div className="flex-grow border-t border-gray-100" />
            </div>
          </>
        )}

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label
              htmlFor="login-email"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              Email
            </label>
            <input
              id="login-email"
              type="email"
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label
                htmlFor="login-password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <Link
                to="/forgotPassword"
                className="text-xs text-[#1d365d] hover:text-[#2d4a75] transition-colors"
              >
                Forgot password?
              </Link>
            </div>
            <input
              id="login-password"
              type="password"
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          {successMessage && (
            <p className="text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              {successMessage}
            </p>
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <PrimaryButton className="w-full" type="submit" disabled={loading}>
            {loading ? "Signing in..." : "Sign in"}
          </PrimaryButton>
        </form>

        <div className="border-t border-gray-100 mt-6 pt-6 text-center">
          <p className="text-sm text-gray-500">
            Don't have an account?{" "}
            <button
              onClick={() => navigate("/pricingPlans")}
              className="text-[#1d365d] font-medium hover:text-[#2d4a75] transition-colors"
            >
              Sign up
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
